import * as THREE from 'three';
import { Panda, type Expression } from '../player/Panda';

// A tiny standalone 3D viewer that shows the actual in-game Panda model on the
// menus, rotatable by dragging (and idly spinning). Renders to its own
// transparent canvas with its own loop only while mounted, so it costs nothing
// during gameplay. Lets the player play with — and us inspect — the panda.
export class PandaViewer {
  readonly canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(38, 1, 0.1, 50);
  private panda = new Panda();

  private rotY = 0.4;
  private rotX = 0.08;
  private fling = 0;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private running = false;
  private last = 0;
  private party = false;
  private clock = 0;

  // Confetti for the "over the moon" party.
  private confetti: THREE.Points;
  private confPos: Float32Array;
  private confVel: Float32Array;
  private readonly CONF = 90;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.touchAction = 'none';
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);

    this.camera.position.set(0, 0.5, 3.5);
    this.camera.lookAt(0, 0.12, 0);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x4a5a3a, 1.15));
    const dir = new THREE.DirectionalLight(0xfff3d6, 1.5);
    dir.position.set(2.5, 3.5, 2.5);
    this.scene.add(dir);
    this.scene.add(this.panda.root);

    // Confetti particle field (hidden unless partying).
    this.confPos = new Float32Array(this.CONF * 3);
    this.confVel = new Float32Array(this.CONF * 3);
    const cols = new Float32Array(this.CONF * 3);
    const palette = [0xff5d5d, 0xffd93d, 0x6dd47e, 0x5db4ff, 0xc56bff, 0xff9a3d];
    const col = new THREE.Color();
    for (let i = 0; i < this.CONF; i++) {
      this.resetConfetti(i, true);
      col.set(palette[(Math.random() * palette.length) | 0]);
      cols[i * 3] = col.r;
      cols[i * 3 + 1] = col.g;
      cols[i * 3 + 2] = col.b;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.confPos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(cols, 3));
    this.confetti = new THREE.Points(
      geo,
      new THREE.PointsMaterial({ size: 0.14, vertexColors: true, transparent: true, depthWrite: false }),
    );
    this.confetti.visible = false;
    this.confetti.frustumCulled = false;
    this.scene.add(this.confetti);

    this.canvas.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  private onDown = (e: PointerEvent) => {
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.fling = 0;
    this.canvas.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  private onMove = (e: PointerEvent) => {
    if (!this.dragging) return;
    const dx = (e.clientX - this.lastX) * 0.01;
    const dy = (e.clientY - this.lastY) * 0.008;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.rotY += dx;
    this.rotX = Math.max(-0.7, Math.min(0.7, this.rotX + dy));
    this.fling = dx;
  };

  private onUp = () => {
    this.dragging = false;
  };

  private resetConfetti(i: number, anywhere = false): void {
    this.confPos[i * 3] = (Math.random() - 0.5) * 2.6;
    this.confPos[i * 3 + 1] = anywhere ? Math.random() * 3.5 - 0.5 : 2 + Math.random() * 1.5;
    this.confPos[i * 3 + 2] = -0.6 + Math.random() * 1.2;
    this.confVel[i * 3] = (Math.random() - 0.5) * 0.6;
    this.confVel[i * 3 + 1] = -(0.7 + Math.random() * 0.8);
    this.confVel[i * 3 + 2] = (Math.random() - 0.5) * 0.3;
  }

  /** Attach into a container, size to it, and start rendering. */
  mount(parent: HTMLElement, expression: Expression = 'content'): void {
    parent.appendChild(this.canvas);
    const px = Math.max(80, Math.round(parent.clientWidth || 160));
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(px, px, false);
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    this.panda.setExpression(expression);
    this.party = expression === 'party';
    this.confetti.visible = this.party;
    this.rotY = 0.4;
    this.rotX = 0.08;
    this.fling = 0;
    this.start();
  }

  unmount(): void {
    this.stop();
    this.canvas.parentElement?.removeChild(this.canvas);
  }

  private start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    requestAnimationFrame(this.loop);
  }

  private stop(): void {
    this.running = false;
  }

  private loop = (now: number): void => {
    if (!this.running) return;
    requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.clock += dt;
    if (!this.dragging) {
      this.rotY += (this.party ? 1.4 : 0.5) * dt + this.fling; // faster spin at the party
      this.fling *= Math.pow(0.02, dt);
    }
    this.panda.root.rotation.set(this.rotX, this.rotY, 0);
    // A happy bounce when partying.
    this.panda.root.position.y = this.party ? Math.abs(Math.sin(this.clock * 6)) * 0.18 : 0;

    if (this.party) {
      for (let i = 0; i < this.CONF; i++) {
        this.confPos[i * 3] += this.confVel[i * 3] * dt;
        this.confPos[i * 3 + 1] += this.confVel[i * 3 + 1] * dt;
        this.confPos[i * 3 + 2] += this.confVel[i * 3 + 2] * dt;
        if (this.confPos[i * 3 + 1] < -1.6) this.resetConfetti(i);
      }
      (this.confetti.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    }

    this.panda.update(dt, 0);
    this.renderer.render(this.scene, this.camera);
  };
}
